
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "@/lib/api/client";
import {
  cancelExportJob,
  createWorkbookExport,
  getExportJob,
  getExportJobDownloadUrl,
  getWorkbookExportsSummary,
} from "@/lib/api/endpoints";

type ExportSummary = {
  sheetCount: number;
  productCount: number;
  fieldCount: number;
  aiRunCount: number;
  totalOutputRows: number;
  warnings: string[];
};

type ExportJob = {
  id: string;
  status: "queued" | "running" | "done" | "error" | "cancelled" | string;
  progress: number;
  totalRows: number;
  processedRows: number;
  notifyEmail: string | null;
  error: string | null;
};

type Props = {
  workbookId: string;
};

const TERMINAL_STATUSES = new Set(["done", "error", "cancelled"]);

function numberFrom(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function stringFrom(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function normalizeSummary(value: unknown): ExportSummary {
  const raw = value && typeof value === "object" ? value as Record<string, unknown> : {};
  return {
    sheetCount: numberFrom(raw.sheetCount),
    productCount: numberFrom(raw.productCount),
    fieldCount: numberFrom(raw.fieldCount),
    aiRunCount: numberFrom(raw.aiRunCount),
    totalOutputRows: numberFrom(raw.totalOutputRows),
    warnings: Array.isArray(raw.warnings)
      ? raw.warnings.filter((w): w is string => typeof w === "string")
      : [],
  };
}

function normalizeJob(value: unknown): ExportJob | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const id = stringFrom(raw.id);
  const status = stringFrom(raw.status);
  if (!id || !status) return null;
  return {
    id,
    status,
    progress: numberFrom(raw.progress),
    totalRows: numberFrom(raw.totalRows),
    processedRows: numberFrom(raw.processedRows),
    notifyEmail: stringFrom(raw.notifyEmail),
    error: stringFrom(raw.error),
  };
}

function isTerminal(status: string | undefined): boolean {
  return status ? TERMINAL_STATUSES.has(status) : false;
}

function compactCount(value: number): string {
  return value.toLocaleString();
}

function SummaryItem({
  label,
  value,
}: {
  label: string;
  value: number;
}) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 dark:border-border dark:bg-surface-2">
      <p className="font-mono text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="mt-1 text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-100">
        {compactCount(value)}
      </p>
    </div>
  );
}

export function WorkbookExportFlow({ workbookId }: Props) {
  const [summary, setSummary] = useState<ExportSummary | null>(null);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<ExportJob | null>(null);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const active = job ? !isTerminal(job.status) : jobId !== null;
  const downloadUrl = job?.status === "done"
    ? getExportJobDownloadUrl(job.id)
    : null;
  const progress = Math.max(0, Math.min(100, job?.progress ?? 0));
  const statusLabel = useMemo(() => {
    if (!job) return "Queued";
    if (job.status === "done") return "Done";
    if (job.status === "error") return "Error";
    if (job.status === "cancelled") return "Cancelled";
    return job.status === "queued" ? "Queued" : "Running";
  }, [job]);

  const openSummary = useCallback(async () => {
    setLoadingSummary(true);
    setError(null);
    try {
      const nextSummary = normalizeSummary(
        await getWorkbookExportsSummary(workbookId, {}),
      );
      if (nextSummary.sheetCount === 0) {
        setError("Bind at least one sheet to a template first.");
        return;
      }
      setSummary(nextSummary);
      setShowSummaryModal(true);
    } catch {
      setError("Export summary failed. Please try again.");
    } finally {
      setLoadingSummary(false);
    }
  }, [workbookId]);

  const pollJob = useCallback(async (id: string) => {
    const nextJob = normalizeJob(await getExportJob(id));
    if (nextJob) setJob(nextJob);
  }, []);

  useEffect(() => {
    if (!jobId || isTerminal(job?.status)) return;

    let cancelled = false;
    const poll = async () => {
      try {
        if (cancelled) return;
        await pollJob(jobId);
      } catch {
        if (!cancelled) setError("Export status failed to refresh.");
      }
    };

    void poll();
    const intervalId = window.setInterval(() => {
      void poll();
    }, 1500);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [job?.status, jobId, pollJob]);

  const startExport = useCallback(async () => {
    setStarting(true);
    setError(null);
    try {
      const payload = notifyEmail.trim().length > 0
        ? { notifyEmail: notifyEmail.trim() }
        : {};
      const data = await createWorkbookExport(workbookId, payload);
      const nextJobId = stringFrom(data.jobId);
      if (!nextJobId) throw new Error("Missing job id");
      setJobId(nextJobId);
      setJob({
        id: nextJobId,
        status: "queued",
        progress: 0,
        processedRows: 0,
        totalRows: summary?.productCount ?? 0,
        notifyEmail: notifyEmail.trim().length > 0 ? notifyEmail.trim() : null,
        error: null,
      });
      setShowSummaryModal(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError("Bind at least one sheet to a template first.");
        return;
      }
      setError("Export failed to start. Please try again.");
    } finally {
      setStarting(false);
    }
  }, [notifyEmail, summary?.productCount, workbookId]);

  const cancelExport = useCallback(async () => {
    if (!jobId) return;
    setCancelling(true);
    setError(null);
    try {
      await cancelExportJob(jobId);
      await pollJob(jobId);
    } catch {
      setError("Cancel request failed.");
    } finally {
      setCancelling(false);
    }
  }, [jobId, pollJob]);

  return (
    <div className="flex w-full flex-col items-end gap-1.5 sm:w-auto">
      <button
        type="button"
        disabled={loadingSummary || starting || active}
        onClick={openSummary}
        title="Export this workbook to a CSV you can upload to your store"
        className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loadingSummary ? (
          <>
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            Getting ready...
          </>
        ) : active ? (
          "Exporting..."
        ) : (
          <>
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M8 2v8" />
              <path d="M4.5 7l3.5 3.5L11.5 7" />
              <path d="M2.5 13.5h11" />
            </svg>
            Export CSV
          </>
        )}
      </button>

      {error ? (
        <p className="max-w-xs text-right text-xs text-red-600 dark:text-red-400">{error}</p>
      ) : null}

      {job ? (
        <div className="mt-2 w-full min-w-[260px] max-w-sm rounded border border-slate-200 bg-white p-3 text-left shadow-sm dark:border-border dark:bg-surface">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">
              {statusLabel}
            </span>
            <span className="font-mono text-[11px] tabular-nums text-slate-500 dark:text-slate-400">
              {progress}%
            </span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-surface-2">
            <div
              className="h-full rounded-full bg-slate-900 transition-all dark:bg-slate-100"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              {compactCount(job.processedRows)} / {compactCount(job.totalRows)} rows
            </span>
            {active ? (
              <button
                type="button"
                onClick={cancelExport}
                disabled={cancelling}
                className="rounded border border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-border dark:text-slate-300 dark:hover:bg-surface-2"
              >
                {cancelling ? "Cancelling..." : "Cancel"}
              </button>
            ) : null}
          </div>
          {downloadUrl ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3 dark:border-border">
              <a
                href={downloadUrl}
                className="rounded bg-slate-900 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-slate-700 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-slate-200"
              >
                Download CSV
              </a>
              {job.notifyEmail ? (
                <span className="text-[11px] text-slate-500 dark:text-slate-400">
                  Emailed to {job.notifyEmail}
                </span>
              ) : null}
            </div>
          ) : null}
          {job.status === "error" ? (
            <p className="mt-2 text-[11px] text-red-600 dark:text-red-400">
              {job.error ?? "Export failed."}
            </p>
          ) : null}
          {job.status === "cancelled" ? (
            <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
              Export cancelled.
            </p>
          ) : null}
        </div>
      ) : null}

      {showSummaryModal && summary ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4 py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="export-summary-heading"
        >
          <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-5 shadow-xl dark:border-border dark:bg-surface">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2
                  id="export-summary-heading"
                  className="text-base font-semibold text-slate-900 dark:text-slate-100"
                >
                  Export summary
                </h2>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {compactCount(summary.sheetCount)} bound sheet{summary.sheetCount === 1 ? "" : "s"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowSummaryModal(false)}
                className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-surface-2 dark:hover:text-slate-200"
                aria-label="Close summary"
              >
                x
              </button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <SummaryItem label="Products" value={summary.productCount} />
              <SummaryItem label="Fields" value={summary.fieldCount} />
              <SummaryItem label="AI runs" value={summary.aiRunCount} />
              <SummaryItem label="Output rows" value={summary.totalOutputRows} />
            </div>

            {summary.warnings.length > 0 ? (
              <div className="mt-4 rounded border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/60 dark:bg-amber-950/30">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                  Shopify may reject or misread some rows ({summary.warnings.length} issue
                  {summary.warnings.length === 1 ? "" : "s"}). You can still export.
                </p>
                <ul className="mt-1.5 max-h-40 list-disc space-y-0.5 overflow-y-auto pl-4 text-[11px] text-amber-900 dark:text-amber-200">
                  {summary.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="mt-4 text-xs text-emerald-700 dark:text-emerald-400">
                No Shopify import issues found.
              </p>
            )}

            <label className="mt-4 block">
              <span className="font-mono text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Notify email
              </span>
              <input
                type="email"
                value={notifyEmail}
                onChange={(event) => setNotifyEmail(event.target.value)}
                placeholder="name@example.com"
                className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-900 dark:border-border dark:bg-surface dark:text-slate-100 dark:focus:border-slate-400"
              />
            </label>

            <div className="mt-5 flex items-center justify-end gap-2 border-t border-slate-100 pt-4 dark:border-border">
              <button
                type="button"
                onClick={() => setShowSummaryModal(false)}
                className="rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 dark:border-border dark:text-slate-300 dark:hover:bg-surface-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={startExport}
                disabled={starting}
                className="rounded bg-slate-900 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-slate-700 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-slate-200"
              >
                {starting ? "Starting..." : "Start export"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
